import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Alert,
  TextField,
  Autocomplete,
  IconButton,
  Chip,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useImportGedcom } from '../../../hooks/treeHooks';
import { useAuth } from '../../../components/auth';
import { ApiResponse } from '../../../types/api';

type GedcomPerson = {
  id: string;
  first_name: string;
  last_name?: string;
  birth?: string;
  gender?: string;
};

const MAX_FILE_SIZE_MB = 10;

type ImportGEDCOMProps = {
  closeModal: () => void;
};

export default function ImportGEDCOM({ closeModal }: ImportGEDCOMProps) {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [people, setPeople] = useState<GedcomPerson[]>([]);
  const [root, setRoot] = useState<GedcomPerson | null>(null);
  const [loading, setLoading] = useState(false);
  const [treeName, setTreeName] = useState('');
  const [description, setDescription] = useState('');

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useImportGedcom();
  const { currentUser } = useAuth();

  const validateFile = (file: File) => {
    if (!file.name.match(/\.(ged|gedcom)$/i))
      return 'Only .ged or .gedcom files are allowed.';
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024)
      return `File must be under ${MAX_FILE_SIZE_MB}MB.`;
    return null;
  };

  const parseGedcom = (text: string): GedcomPerson[] => {
    const lines = text.split('\n');
    const persons: GedcomPerson[] = [];

    let current: Partial<GedcomPerson> | null = null;

    for (const line of lines) {
      const l = line.trim();

      if (l.match(/^0 @.+@ INDI/)) {
        if (current) persons.push(current as GedcomPerson);
        const id = l.split(' ')[1];
        current = { id, first_name: 'Unknown' };
        continue;
      }

      if (!current) continue;

      if (l.startsWith('1 NAME')) {
        const raw = l.replace('1 NAME', '').trim();
        const cleaned = raw.replace(/\//g, '');
        const parts = cleaned.split(' ');
        current.first_name = parts[0] || 'Unknown';
        current.last_name = parts.slice(1).join(' ') || undefined;
      }

      if (l.startsWith('1 SEX')) {
        current.gender = l.replace('1 SEX', '').trim();
      }

      if (l.startsWith('2 DATE')) {
        current.birth = l.replace('2 DATE', '').trim();
      }
    }

    if (current) persons.push(current as GedcomPerson);

    return persons.sort((a, b) =>
      `${a.first_name} ${a.last_name || ''}`.localeCompare(
        `${b.first_name} ${b.last_name || ''}`,
      ),
    );
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    const validationError = validateFile(selected);
    if (validationError) {
      setError(validationError);
      setFile(null);
      return;
    }

    setError('');
    setFile(selected);
    setRoot(null);
    setPeople([]);
    setLoading(true);

    const text = await selected.text();
    const parsed = parseGedcom(text);

    setPeople(parsed);
    setRoot(parsed[0] ?? null);
    setLoading(false);
  };

  function handleImport() {
    if (!currentUser || !file || !root || !treeName.trim()) return;

    setLoading(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('root_person_id', root.id);
    formData.append('tree_name', treeName.trim());
    formData.append('created_by', currentUser.uid);
    if (description.trim()) {
      formData.append('description', description.trim());
    }

    mutation.mutate(formData, {
      onSuccess: (response: ApiResponse<{ id: string }>) => {
        setLoading(false);
        enqueueSnackbar('GEDCOM file imported successfully', {
          variant: 'success',
        });

        if ('id' in response) {
          navigate(`/app/tree/${response.id}`);
        } else if (response.data && 'id' in response.data) {
          navigate(`/app/tree/${response.data.id}`);
        }

        closeModal?.();
      },
      onError: (error: any) => {
        setLoading(false);
        console.error('Error importing GEDCOM file:', error);
        const msg = error.message || 'An unexpected error occurred.';
        setError(msg);
        enqueueSnackbar(`Error importing GEDCOM file: ${msg}`, {
          variant: 'error',
        });
      },
    });
  }

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        handleImport();
      }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          px: { xs: 3, md: 4 },
          pt: { xs: 3, md: 4 },
          pb: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            Import a family tree
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload a GEDCOM file, choose the root person, and add details for
            your tree.
          </Typography>
        </Box>
        <IconButton onClick={closeModal} size="small" sx={{ mt: 0.5 }}>
          <ClearIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: { xs: 3, md: 4 },
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}

        <Box
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              GEDCOM file
            </Typography>
            <Chip
              label="Required"
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Choose a .ged or .gedcom file. Maximum file size is{' '}
            {MAX_FILE_SIZE_MB}MB.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            disabled={loading}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              alignSelf: 'flex-start',
            }}
          >
            {file ? 'Choose a different file' : 'Upload GEDCOM file'}
            <input
              hidden
              type="file"
              accept=".ged,.gedcom"
              onChange={handleFileChange}
            />
          </Button>
          {file && (
            <Typography variant="body2" color="text.secondary">
              {file.name} · {(file.size / 1024 / 1024).toFixed(2)}MB
            </Typography>
          )}
          {loading && <LinearProgress />}
        </Box>

        <TextField
          label="Tree name"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          placeholder="e.g. The Smith Family Tree"
          fullWidth
          value={treeName}
          onChange={(event) => setTreeName(event.target.value)}
          required
          error={!treeName.trim() && treeName !== ''}
        />

        <TextField
          label="Description"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          placeholder="Add an optional description of the family tree"
          fullWidth
          multiline
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />

        {people.length > 0 && (
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              border: '2px solid',
              borderColor: 'primary.light',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  Choose the root person
                </Typography>
                <Chip
                  label="Required"
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {people.length} people found in this file.
              </Typography>
            </Box>
            <Autocomplete
              options={people}
              value={root}
              onChange={(_, value) => setRoot(value)}
              getOptionLabel={(option) =>
                `${option.first_name} ${option.last_name || ''}${
                  option.birth ? ` (${option.birth})` : ''
                }`
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select root person"
                  placeholder="Search by name..."
                  fullWidth
                />
              )}
            />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          px: { xs: 3, md: 4 },
          py: 2.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Button
          type="button"
          variant="outlined"
          onClick={closeModal}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!file || !root || !treeName.trim() || loading}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            minWidth: 120,
          }}
        >
          {loading ? 'Importing…' : 'Import family tree'}
        </Button>
      </Box>
    </Box>
  );
}
